import { useTranslation } from 'react-i18next'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import ReservationAuditCRUD from './reservation-audit.crud'
import { HicDataGridPremium } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { GridColDef } from '@mui/x-data-grid'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { AuditFragment, ChargeCategoryFragment, FiscalType, ReservedResourcePhase, ResourceTypeAuditFragment, UserFragment } from 'src/api/api.generated'
import getUserAudit from 'src/app/pages/audits/utils/get-user-audit'
import translateOperationName from 'src/app/pages/audits/utils/translate-operation-name'
import translateOperation from 'src/app/pages/audits/utils/translate-operation'
import translateEntityName from 'src/app/pages/audits/utils/translate-entity-name'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { Currency } from 'src/api/currency'
import { Button, MenuItem, Select, Stack, Typography } from '@vapor/material'
import { displayResourceType } from 'src/utils/resource-type'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import getFiscalTypeDescription from 'src/app/pages/accounting/documents/documents-utils'
import { useState } from 'react'
import { FormItem } from 'hic-components'
import { TFunction } from 'i18next'
import ChangesDialog from 'src/app/pages/audits/components/changes-dialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCode } from '@fortawesome/pro-regular-svg-icons'
import { IfUserInRole } from 'src/app/components/authorization'
import { getChanges } from './get-changes-from-audit'

export default function ReservationAuditDrawer({
  open,
  onClose,
  propertyId,
  tenantId,
  productionIds,
  reservedResourceIds,
  reservationId,
  billIds,
  documentIds,
  reservationCode,
  description,
}: {
  open: boolean
  onClose: () => void
  tenantId: string
  propertyId: string
  productionIds: string[]
  reservedResourceIds: string[]
  reservationId: string
  billIds: string[]
  documentIds: string[]
  reservationCode: string
  description: string
}) {
  const { t } = useTranslation()
  const formatDate = useFormatDate()

  const [timestampFilter, setTimestampFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [operationNameFilter, setOperationNameFilter] = useState('')
  const [operationFilter, setOperationFilter] = useState('')
  const [entityNameFilter, setEntityNameFilter] = useState('')

  const [auditToShow, setAuditToShow] = useState<AuditFragment | null>(null)

  const { audits, users, loading, chargeCategories, resourceTypes, vats, reservationStates } = ReservationAuditCRUD({
    propertyId,
    tenantId,
    productionIds,
    reservedResourceIds,
    reservationId,
    billIds,
    documentIds,
  })

  const columns = useColumns({ users, audits, chargeCategories, resourceTypes, reservationId, onShowJSON: setAuditToShow })

  const timestampOptions = [...new Set(audits.map((audit) => formatTimestamp({ timestamp: audit.timestamp, t, formatDate })))]
  const userOptions = [...new Set(audits.map((audit) => audit.modifiedBy))]
  const operationNameOptions = [...new Set(audits.map((audit) => audit.operationName))]
  const operationOptions = [...new Set(audits.map((audit) => audit.operation))]
  const entityNameOptions = [...new Set(audits.map((audit) => audit.entityName))]

  const filteredAudits = audits.filter((audit) => {
    if (timestampFilter && formatTimestamp({ timestamp: audit.timestamp, t, formatDate }) !== timestampFilter) {
      return false
    }

    if (userFilter && audit.modifiedBy !== userFilter) {
      return false
    }

    if (operationNameFilter && audit.operationName !== operationNameFilter) {
      return false
    }

    if (operationFilter && audit.operation !== operationFilter) {
      return false
    }

    if (entityNameFilter && audit.entityName !== entityNameFilter) {
      return false
    }

    return true
  })

  return (
    <>
      <FormLayoutDrawer
        open={open}
        onClose={onClose}
        large
        title={t('pms.booking.reservation.header.menu.audit')}
        titleOnLeft
        toolbar={
          <FormLayoutDefaultToolbar
            cancelButton={{
              clickHandler: onClose,
              text: t('commons.close'),
              disabled: loading,
              loading,
            }}
          />
        }
      >
        <Typography marginBottom={1}>
          {t('pms.booking.reservation.audit.reservation')} #{reservationCode} ({description})
        </Typography>
        <Stack direction="row" gap={2} marginBottom={2}>
          <FormItem fieldLabel={t('pms.booking.reservation.audit.timestamp')}>
            <Select
              sx={{ width: 200 }}
              size="small"
              value={timestampFilter}
              onChange={(event) => {
                setTimestampFilter(event.target.value as string)
              }}
            >
              <MenuItem value=""></MenuItem>
              {timestampOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormItem>
          <FormItem fieldLabel={t('pms.audits.grid.user')}>
            <Select
              sx={{ width: 200 }}
              size="small"
              value={userFilter}
              onChange={(event) => {
                setUserFilter(event.target.value as string)
              }}
            >
              <MenuItem value=""></MenuItem>
              {userOptions.map((option) => {
                if (!option) return null

                return (
                  <MenuItem value={option} key={option}>
                    {getUserAudit({ userId: option, users, t })}
                  </MenuItem>
                )
              })}
            </Select>
          </FormItem>
          <FormItem fieldLabel={t('pms.audits.grid.correaltion')}>
            <Select
              sx={{ width: 200 }}
              size="small"
              value={operationNameFilter}
              onChange={(event) => {
                setOperationNameFilter(event.target.value as string)
              }}
            >
              <MenuItem value=""></MenuItem>
              {operationNameOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  {translateOperationName({ operationName: option, t })}
                </MenuItem>
              ))}
            </Select>
          </FormItem>
          <FormItem fieldLabel={t('pms.audits.grid.operation')}>
            <Select
              sx={{ width: 200 }}
              size="small"
              value={operationFilter}
              onChange={(event) => {
                setOperationFilter(event.target.value as string)
              }}
            >
              <MenuItem value=""></MenuItem>
              {operationOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  {translateOperation({ operation: option, t })}
                </MenuItem>
              ))}
            </Select>
          </FormItem>
          <FormItem fieldLabel={t('pms.audits.grid.entity')}>
            <Select
              sx={{ width: 200 }}
              size="small"
              value={entityNameFilter}
              onChange={(event) => {
                setEntityNameFilter(event.target.value as string)
              }}
            >
              <MenuItem value=""></MenuItem>
              {entityNameOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  {translateEntityName({ entityName: option, t })}
                </MenuItem>
              ))}
            </Select>
          </FormItem>
        </Stack>
        <HicDataGridPremium pagination={false} autoHeight hideFooter loading={loading} rows={filteredAudits} columns={columns} getRowHeight={() => 'auto'} />
      </FormLayoutDrawer>
      {auditToShow !== null && (
        <ChangesDialog
          open={auditToShow !== null}
          onClose={() => {
            setAuditToShow(null)
          }}
          audit={auditToShow}
          reservationStates={reservationStates}
          chargeCategories={chargeCategories}
          vats={vats}
          propertyId={propertyId}
          tenantId={tenantId}
        />
      )}
    </>
  )
}

function useColumns({
  users,
  audits,
  chargeCategories,
  resourceTypes,
  reservationId,
  onShowJSON,
}: {
  users: UserFragment[]
  audits: AuditFragment[]
  chargeCategories: ChargeCategoryFragment[]
  resourceTypes: ResourceTypeAuditFragment[]
  reservationId: string
  onShowJSON: (audit: AuditFragment) => void
}): GridColDef[] {
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const { formatCurrency } = useFormatCurrency()
  const localizeString = useLocalizeString()

  return [
    {
      field: 'timestamp',
      headerName: t('pms.booking.reservation.audit.timestamp'),
      disableColumnMenu: true,
      sortable: false,
      width: 160,
      valueFormatter: (params) => formatTimestamp({ timestamp: params.value, t, formatDate }),
    },
    {
      field: 'modifiedBy',
      headerName: t('pms.audits.grid.user'),
      sortable: false,
      disableColumnMenu: true,
      width: 160,
      valueGetter: (params) => getUserAudit({ userId: params.value, users, t }),
    },
    {
      field: 'operationName',
      headerName: t('pms.audits.grid.correaltion'),
      sortable: false,
      disableColumnMenu: true,
      width: 190,
      valueGetter: (params) => translateOperationName({ operationName: params.value, t }),
    },
    {
      field: 'operation',
      headerName: t('pms.audits.grid.operation'),
      sortable: false,
      disableColumnMenu: true,
      width: 125,
      valueGetter: (params) => translateOperation({ operation: params.value, t }),
    },
    {
      field: 'entityName',
      headerName: t('pms.audits.grid.entity'),
      sortable: false,
      disableColumnMenu: true,
      width: 110,
      valueGetter: (params) => translateEntityName({ entityName: params.value, t }),
    },
    {
      field: 'changes',
      headerName: t('pms.booking.reservation.audit.changes'),
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => {
        let content: React.ReactNode = null
        const audit = params.row as AuditFragment

        if (audit.entityName === 'Production') {
          const changes = getChanges({ audit: params.row, audits, changesToGet: ['amount', 'date', 'chargeCategoryId', 'quantity', 'total', 'currency'] }) as {
            amount: number
            date: string
            chargeCategoryId: string
            quantity: number
            total: number
            currency: Currency
          }

          if (changes) {
            const chargeCategory = chargeCategories.find((chargeCategoryToCheck) => chargeCategoryToCheck.id === changes.chargeCategoryId)

            content = (
              <>
                <Typography variant="caption">
                  {formatCurrency(changes.amount, changes.currency)} x {changes.quantity} ({t('pms.booking.reservation.audit.changes.total')} {formatCurrency(changes.total, changes.currency)})
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.competence')}: {formatDate(new Date(changes.date))}
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.charge_category')}: {chargeCategory?.name ?? ''}
                </Typography>
              </>
            )
          }
        } else if (audit.entityName === 'ReservedResource') {
          const changes = getChanges({ audit: params.row, audits, changesToGet: ['start', 'end', 'resourceOccupancies', 'adults', 'children', 'phase'] }) as {
            start: string
            end: string
            resourceOccupancies: {
              assignedResourceTypeId: string
              resourceId: string | null
            }[]
            adults: number
            children: number
            phase: ReservedResourcePhase
          }

          if (changes) {
            // TODO far vedere i trasferimenti e in base a quello far vedere la tipologia
            const resourceType = resourceTypes.find((resourceTypeToCheck) => resourceTypeToCheck.id === changes.resourceOccupancies?.[0]?.assignedResourceTypeId)
            const resource = resourceType?.resources?.find((resourceToCheck) => resourceToCheck.id === changes.resourceOccupancies?.[0]?.resourceId)

            content = (
              <>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.resource')}: {resource?.code ?? '---'} ({resourceType ? displayResourceType(localizeString, resourceType) : ''})
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.dates')}: {formatDate(new Date(changes.start))} - {formatDate(new Date(changes.end))}
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.guests')}: {(changes.adults ?? 0) + (changes.children ?? 0)}
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.phase')}: {t(`pms.booking.reservation.audit.changes.phase.${changes.phase}`)}
                </Typography>
              </>
            )
          }
        } else if (audit.entityName === 'Bill') {
          const changes = getChanges({ audit: params.row, audits, changesToGet: ['description', 'searchInfo'] }) as {
            description: string
            searchInfo: {
              ownerName: string
              resources: string[]
            }
          }

          if (changes) {
            content = (
              <>
                {!changes.description.includes(reservationId) && (
                  <Typography variant="caption">
                    {t('pms.booking.reservation.audit.changes.bill')}: {changes.description}
                  </Typography>
                )}
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.owner')}: {changes?.searchInfo?.ownerName}
                </Typography>
                {Boolean(changes?.searchInfo?.resources?.length) && (
                  <Typography variant="caption">
                    {t('pms.booking.reservation.audit.changes.resources')}: {changes?.searchInfo?.resources?.join(', ')}
                  </Typography>
                )}
              </>
            )
          }
        } else if (audit.entityName === 'Document') {
          const changes = getChanges({ audit: params.row, audits, changesToGet: ['fiscalType', 'totalAmount', 'unpaidAmount', 'currency', 'numberExtended'] }) as {
            fiscalType: FiscalType
            totalAmount: number
            unpaidAmount: number
            currency: Currency
            numberExtended: string
          }

          if (changes) {
            content = (
              <>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.document')}: {changes.numberExtended}
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.fiscal_type')}: {getFiscalTypeDescription(changes.fiscalType, t)}
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.Total')}: {formatCurrency(changes.totalAmount, changes.currency)}
                </Typography>
                <Typography variant="caption">
                  {t('pms.booking.reservation.audit.changes.paid')}: {formatCurrency(changes.totalAmount - changes.unpaidAmount, changes.currency)}
                </Typography>
              </>
            )
          }
        }

        return (
          <Stack paddingY={1} alignItems="start">
            {content}
            <IfUserInRole roles={['SUPPORT']}>
              <Button
                variant="text"
                sx={{ fontSize: '12px !important', minHeight: '0px !important', height: 'unset !important', padding: '0px !important' }}
                onClick={() => {
                  onShowJSON(audit)
                }}
              >
                <FontAwesomeIcon icon={faCode} style={{ marginRight: '4px' }} />
                {t('pms.booking.reservation.audit.json')}
              </Button>
            </IfUserInRole>
          </Stack>
        )
      },
    },
  ]
}

function formatTimestamp({ timestamp, t, formatDate }: { timestamp: Date; t: TFunction; formatDate: (date: Date, format?: string) => string }) {
  return t('pms.booking.reservation.audit.at', { date: formatDate(timestamp), hour: formatDate(timestamp, 'p') })
}
